import React, { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { getWorkflowAudio } from 'services/api/WorkflowServiceAPI';
import { Modal } from 'common/components/Modal';
import { CallInfo } from 'types/agentCommandCenter.type';
import 'components/TranscriptModal/TranscriptModal.scss'; // Reusing the same styles for consistent styling

interface AudioModalProps {
    isOpen: boolean;
    onClose: () => void;
    callId: string | null;
    callInfo?: CallInfo;
    callDetailsNavigation?: React.ReactNode;
    isLoading?: boolean;
}

/**
 * Reusable component for playing call audio recordings
 */
const AudioModal: React.FC<AudioModalProps> = ({
    isOpen,
    onClose,
    callId,
    callInfo = {},
    callDetailsNavigation,
    isLoading,
}) => {
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch audio data when callId changes
    useEffect(() => {
        if (!isOpen || !callId) return;

        // Clean up previous audio URL if it exists
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }

        const fetchAudio = async () => {
            try {
                setLoading(true);
                setError(null);

                const audioData = await getWorkflowAudio(callId);

                if (audioData) {
                    const url = URL.createObjectURL(audioData);
                    setAudioUrl(url);
                }
            } catch (err) {
                console.error('Error fetching audio:', err);
                setError('Failed to load audio. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchAudio();

        // Cleanup function to revoke object URL when component unmounts
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [isOpen, callId]);

    const title = (
        <div className='flex justify-between items-center'>
            <h2>Call Recording</h2>
            {callDetailsNavigation}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size='lg'
            showDefaultFooter={false}
            footer={
                <button onClick={onClose} className='btn'>
                    Close
                </button>
            }
            isLoadingOverlay={isLoading}
        >
            <div className='transcript-header'>
                <div className='transcript-title'>{callInfo?.name || 'Unknown Contact'}</div>
                <div className='transcript-meta'>
                    <span>Date: {callInfo?.callDate || 'Unknown'}</span>
                    <span>Time: {callInfo?.callTime || 'Unknown'}</span>
                </div>
            </div>

            {loading ? (
                <div className='transcript-empty'>Loading audio...</div>
            ) : error ? (
                <div className='transcript-empty error'>{error}</div>
            ) : audioUrl ? (
                <div className='audio-container'>
                    <audio controls className='audio-player'>
                        <source src={audioUrl} type='audio/ogg; codecs=opus' />
                        <source src={audioUrl} type='audio/ogg' />
                        <source src={audioUrl} type='audio/mpeg' />
                        Your browser does not support the audio element.
                    </audio>
                </div>
            ) : (
                <div className='transcript-empty'>No audio recording available for this call.</div>
            )}
        </Modal>
    );
};

export default AudioModal;
